import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import Navbar from "./Navbar";
import { DEFAULT_TAX_RATE } from "../constants/tax";
import {
  FaChartBar,
  FaBalanceScale,
  FaReceipt,
  FaClipboardList,
} from "react-icons/fa";

const Finance = () => {
  const [activeSection, setActiveSection] = useState("main");
  const navigate = useNavigate();

  const mainCards = [
    {
      id: "chart-accounts",
      title: "Chart of Accounts Menu",
      icon: FaChartBar,
      description: "Manage and view your chart of accounts",
      color: "bg-blue-500",
    },
    {
      id: "trial-balance",
      title: "Trial Balance - Detail Balance",
      icon: FaBalanceScale,
      description: "View detailed trial balance reports",
      color: "bg-green-500",
    },
    {
      id: "finance-statements",
      title: "Finance Statements",
      icon: FaReceipt,
      description: "Generate income and balance statements",
      color: "bg-purple-500",
    },
    {
      id: "hst-report",
      title: "HST Report",
      icon: FaReceipt,
      description: "Generate and view HST reports",
      color: "bg-orange-500",
    },
    {
      id: "activity-details-report",
      title: "Activity Details Report",
      icon: FaClipboardList,
      description: "Commission activity by G/L for the period",
      color: "bg-teal-500",
    },
    {
      id: "open-trades-commission-report",
      title: "Open Trades Commission Report",
      icon: FaChartBar,
      description: "Commission breakdown for trades opened in a period",
      color: "bg-indigo-500",
    },
    {
      id: "commission-report",
      title: "Commission Report",
      icon: FaChartBar,
      description: "Commission by agent, classification, and date",
      color: "bg-sky-600",
    },
    {
      id: "pending-trade-report",
      title: "Pending Trade Report",
      icon: FaClipboardList,
      description: "Pending listing and selling ends by agent",
      color: "bg-cyan-600",
    },
    {
      id: "custom-report",
      title: "Custom Report",
      icon: FaClipboardList,
      description: "Editable trade report by closing-date period",
      color: "bg-slate-700",
    },
    {
      id: "invoice-detail-report",
      title: "Invoice Detail Report",
      icon: FaClipboardList,
      description: "Monthly invoice detail by closing date",
      color: "bg-indigo-700",
    },
  ];

  const handleCardClick = (cardId) => {
    if (cardId === "chart-accounts") {
      navigate("/chart-of-accounts");
    } else if (cardId === "trial-balance") {
      navigate("/trial-balance");
    } else if (cardId === "finance-statements") {
      navigate("/finance-statements");
    } else if (cardId === "hst-report") {
      navigate("/hst-report");
    } else if (cardId === "activity-details-report") {
      navigate("/finance-activity-details");
    } else if (cardId === "open-trades-commission-report") {
      navigate("/finance-open-trades-commission");
    } else if (cardId === "commission-report") {
      navigate("/finance-commission-report");
    } else if (cardId === "pending-trade-report") {
      navigate("/finance-pending-trade-report");
    } else if (cardId === "custom-report") {
      navigate("/finance-custom-report");
    } else if (cardId === "invoice-detail-report") {
      navigate("/finance-invoice-detail-report");
    } else {
      setActiveSection(cardId);
    }
  };

  const renderMainContent = () => {
    switch (activeSection) {
      case "chart-accounts":
        return (
          <div className="bg-white p-6 rounded-lg shadow-md">
            <h2 className="text-2xl font-bold mb-4 text-gray-800">
              Chart of Accounts Menu
            </h2>
            <p className="text-gray-600 mb-4">
              Manage your chart of accounts, add new accounts, and organize your
              financial structure.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="p-4 border border-gray-200 rounded-lg">
                <h3 className="font-semibold mb-2">Asset Accounts</h3>
                <ul className="text-sm text-gray-600">
                  <li>• Cash and Cash Equivalents</li>
                  <li>• Accounts Receivable</li>
                  <li>• Prepaid Expenses</li>
                  <li>• Fixed Assets</li>
                </ul>
              </div>
              <div className="p-4 border border-gray-200 rounded-lg">
                <h3 className="font-semibold mb-2">Liability Accounts</h3>
                <ul className="text-sm text-gray-600">
                  <li>• Accounts Payable</li>
                  <li>• Accrued Expenses</li>
                  <li>• Bank Loans</li>
                  <li>• HST Payable</li>
                </ul>
              </div>
            </div>
          </div>
        );
      case "trial-balance":
        return (
          <div className="bg-white p-6 rounded-lg shadow-md">
            <h2 className="text-2xl font-bold mb-4 text-gray-800">
              Trial Balance - Detail Balance
            </h2>
            <p className="text-gray-600 mb-4">
              View detailed trial balance reports with account balances and
              adjustments.
            </p>
            <div className="overflow-x-auto">
              <table className="min-w-full border border-gray-300">
                <thead>
                  <tr className="bg-gray-50">
                    <th className="border border-gray-300 px-4 py-2 text-left">
                      Account
                    </th>
                    <th className="border border-gray-300 px-4 py-2 text-right">
                      Debit
                    </th>
                    <th className="border border-gray-300 px-4 py-2 text-right">
                      Credit
                    </th>
                    <th className="border border-gray-300 px-4 py-2 text-right">
                      Balance
                    </th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td className="border border-gray-300 px-4 py-2">Cash</td>
                    <td className="border border-gray-300 px-4 py-2 text-right">
                      $50,000
                    </td>
                    <td className="border border-gray-300 px-4 py-2 text-right">
                      -
                    </td>
                    <td className="border border-gray-300 px-4 py-2 text-right">
                      $50,000
                    </td>
                  </tr>
                  <tr>
                    <td className="border border-gray-300 px-4 py-2">
                      Accounts Receivable
                    </td>
                    <td className="border border-gray-300 px-4 py-2 text-right">
                      $25,000
                    </td>
                    <td className="border border-gray-300 px-4 py-2 text-right">
                      -
                    </td>
                    <td className="border border-gray-300 px-4 py-2 text-right">
                      $25,000
                    </td>
                  </tr>
                  <tr>
                    <td className="border border-gray-300 px-4 py-2">
                      Accounts Payable
                    </td>
                    <td className="border border-gray-300 px-4 py-2 text-right">
                      -
                    </td>
                    <td className="border border-gray-300 px-4 py-2 text-right">
                      $15,000
                    </td>
                    <td className="border border-gray-300 px-4 py-2 text-right">
                      ($15,000)
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        );
      case "hst-report":
        return (
          <div className="bg-white p-6 rounded-lg shadow-md">
            <h2 className="text-2xl font-bold mb-4 text-gray-800">
              HST Report
            </h2>
            <p className="text-gray-600 mb-4">
              Generate and view HST (Harmonized Sales Tax) reports for tax
              filing.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="p-4 border border-gray-200 rounded-lg">
                <h3 className="font-semibold mb-3 text-blue-600">
                  HST Collected
                </h3>
                <div className="space-y-2">
                  <div className="flex justify-between">
                    <span>Total Sales (HST Included):</span>
                    <span className="font-semibold">$100,000</span>
                  </div>
                  <div className="flex justify-between">
                    <span>
                      HST Collected ({Math.round(DEFAULT_TAX_RATE * 100)}%):
                    </span>
                    <span className="font-semibold text-green-600">
                      $13,000
                    </span>
                  </div>
                </div>
              </div>
              <div className="p-4 border border-gray-200 rounded-lg">
                <h3 className="font-semibold mb-3 text-red-600">HST Paid</h3>
                <div className="space-y-2">
                  <div className="flex justify-between">
                    <span>Total Purchases (HST Included):</span>
                    <span className="font-semibold">$50,000</span>
                  </div>
                  <div className="flex justify-between">
                    <span>
                      HST Paid ({Math.round(DEFAULT_TAX_RATE * 100)}%):
                    </span>
                    <span className="font-semibold text-red-600">$6,500</span>
                  </div>
                </div>
              </div>
            </div>
            <div className="mt-6 p-4 bg-gray-50 rounded-lg">
              <h3 className="font-semibold mb-2">Net HST Owing</h3>
              <div className="text-2xl font-bold text-blue-600">
                $6,500 (HST Collected - HST Paid)
              </div>
            </div>
          </div>
        );
      case "trust-payments":
        return (
          <div className="bg-white p-6 rounded-lg shadow-md">
            <h2 className="text-2xl font-bold mb-4 text-gray-800">
              Real Estate Trust Payments
            </h2>
            <p className="text-gray-600 mb-4">
              Manage real estate trust account payments and transactions.
            </p>
            <div className="space-y-4">
              <div className="p-4 border border-gray-200 rounded-lg">
                <h3 className="font-semibold mb-2">Trust Account Summary</h3>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="text-gray-600">Current Balance:</span>
                    <span className="font-semibold ml-2">$150,000</span>
                  </div>
                  <div>
                    <span className="text-gray-600">Pending Payments:</span>
                    <span className="font-semibold ml-2">$25,000</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        );
      case "commission-trust":
        return (
          <div className="bg-white p-6 rounded-lg shadow-md">
            <h2 className="text-2xl font-bold mb-4 text-gray-800">
              Commission Trust Payments
            </h2>
            <p className="text-gray-600 mb-4">
              Manage commission trust account payments and distributions.
            </p>
            <div className="space-y-4">
              <div className="p-4 border border-gray-200 rounded-lg">
                <h3 className="font-semibold mb-2">Commission Trust Summary</h3>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="text-gray-600">Total Commissions:</span>
                    <span className="font-semibold ml-2">$75,000</span>
                  </div>
                  <div>
                    <span className="text-gray-600">Paid to Agents:</span>
                    <span className="font-semibold ml-2">$60,000</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        );
      case "commission-trust-ledger":
        return (
          <div className="bg-white p-6 rounded-lg shadow-md">
            <h2 className="text-2xl font-bold mb-4 text-gray-800">
              Commission Trust Ledger
            </h2>
            <p className="text-gray-600 mb-4">
              View detailed ledger entries for the commission trust account.
            </p>
            <div className="overflow-x-auto">
              <table className="min-w-full border border-gray-300">
                <thead>
                  <tr className="bg-gray-50">
                    <th className="border border-gray-300 px-4 py-2 text-left">
                      Date
                    </th>
                    <th className="border border-gray-300 px-4 py-2 text-left">
                      Description
                    </th>
                    <th className="border border-gray-300 px-4 py-2 text-right">
                      Debit
                    </th>
                    <th className="border border-gray-300 px-4 py-2 text-right">
                      Credit
                    </th>
                    <th className="border border-gray-300 px-4 py-2 text-right">
                      Balance
                    </th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td className="border border-gray-300 px-4 py-2">
                      2023-10-20
                    </td>
                    <td className="border border-gray-300 px-4 py-2">
                      Commission Payment to Agent A
                    </td>
                    <td className="border border-gray-300 px-4 py-2 text-right">
                      $10,000
                    </td>
                    <td className="border border-gray-300 px-4 py-2 text-right">
                      -
                    </td>
                    <td className="border border-gray-300 px-4 py-2 text-right">
                      $10,000
                    </td>
                  </tr>
                  <tr>
                    <td className="border border-gray-300 px-4 py-2">
                      2023-10-25
                    </td>
                    <td className="border border-gray-300 px-4 py-2">
                      Commission Payment to Agent B
                    </td>
                    <td className="border border-gray-300 px-4 py-2 text-right">
                      $15,000
                    </td>
                    <td className="border border-gray-300 px-4 py-2 text-right">
                      -
                    </td>
                    <td className="border border-gray-300 px-4 py-2 text-right">
                      $25,000
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        );
      case "general-account":
        return (
          <div className="bg-white p-6 rounded-lg shadow-md">
            <h2 className="text-2xl font-bold mb-4 text-gray-800">
              General Account Payments (A/P) Expenses
            </h2>
            <p className="text-gray-600 mb-4">
              Manage accounts payable and general expense payments.
            </p>
            <div className="space-y-4">
              <div className="p-4 border border-gray-200 rounded-lg">
                <h3 className="font-semibold mb-2">Accounts Payable Summary</h3>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="text-gray-600">Total Outstanding:</span>
                    <span className="font-semibold ml-2">$45,000</span>
                  </div>
                  <div>
                    <span className="text-gray-600">Overdue Amount:</span>
                    <span className="font-semibold ml-2 text-red-600">
                      $12,000
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        );
      default:
        return (
          <div className="bg-white p-6 rounded-lg shadow-md">
            <h2 className="text-xl font-semibold mb-6 text-gray-800">
              Finance Dashboard
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {mainCards.map((card) => (
                <div
                  key={card.id}
                  className="bg-white shadow-md hover:shadow-lg transition rounded-lg p-6 flex flex-col items-center cursor-pointer"
                  onClick={() => handleCardClick(card.id)}
                >
                  <card.icon className="text-4xl text-blue-900 mb-2" />
                  <h3 className="text-lg font-semibold text-gray-700">
                    {card.title}
                  </h3>
                </div>
              ))}
            </div>
          </div>
        );
    }
  };

  return (
    <div className="bg-gray-50 min-h-screen">
      <Navbar />
      <div className="flex-1 p-6">{renderMainContent()}</div>
    </div>
  );
};

export default Finance;
